import { uniqueId } from 'lodash';
import React, { createContext, useState, ReactNode, useCallback, useRef, JSX } from 'react';

/*
A session ID is generated on login, and used to track actions for Datadog logging
*/
export type SessionCtx = {
  baseId: string | null;
  sessionId: string | null;
  startSession: () => void;
  endSession: () => void;
  appendUniqueId: () => void;
};

const SessionContext = createContext<SessionCtx>({
  baseId: null,
  sessionId: null,
  startSession: () => {},
  endSession: () => {},
  appendUniqueId: () => {},
});

export const SessionProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [baseId, setBaseId] = useState<string | null>(null);

  // since this can trigger multiple times, prevents a race condition between startSession calls.
  const inUseRef = useRef(false);

  const startSession = useCallback(() => {
    if (inUseRef.current) return;

    inUseRef.current = true;

    const id = String(Date.now());
    setBaseId(id);
    setSessionId(`${id}-${uniqueId()}`); // have to set manually to avoid race condition
  }, [sessionId, baseId]);

  const endSession = useCallback(() => {
    setSessionId(null);
    inUseRef.current = false;
  }, []);

  const appendUniqueId = useCallback(() => {
    if (baseId === null) return;
    const id = `${baseId}-${uniqueId()}`;
    setSessionId(id);
  }, [sessionId, baseId]);

  return (
    <SessionContext.Provider value={{ baseId, sessionId, startSession, endSession, appendUniqueId }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionCtx => React.useContext(SessionContext);
